import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import {
  ManagerProgressionTaskState,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BrandXS, LabelS } from '@sorare/core/src/atoms/typography';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { isType } from '@sorare/core/src/lib/gql';
import {
  useCommonGameSet,
  useGetDisplayCardSetName,
} from '@sorare/core/src/set';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ClaimButton } from '#src/components/missions/UnclaimedTask/ClaimButton/index.tsx';
import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import { SetCollectionTaskDescription } from './SetCollectionTaskDescription/index.tsx';
import type { SetCollectionTask_taskInterface } from './__generated__/index.graphql.ts';

const Container = styled(LinkBox)`
  width: 100%;
  position: relative;
  &.claimed {
    opacity: 0.5;
  }
  &.selected {
    opacity: 1;
  }
  transition: all 0.2s ease-in-out;
`;

const CardContent = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  position: relative;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const StyledLightedBezel = styled(LightedBezel)`
  width: 100%;
`;

const StyledLinkOverlay = styled(LinkOverlay)`
  width: 100%;
  text-align: left;
  cursor: pointer;
`;

const InProgressTaskWrapper = styled(LinkBox)`
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  background: var(--c-nl-200);
  text-align: left;
  position: relative;
  transition: all 0.2s ease-in-out;
  &.selected {
    background: var(--c-nd-50);
    border: 1px solid white;
  }
`;

const ProgressSection = styled(Horizontal).attrs({ gap: 1 })`
  flex: 1;
  align-items: center;
  min-width: 0;
`;

const StyledProgress = styled(Progress)`
  flex: 1;
  min-width: 0;
`;

const ContentWrapper = styled(Vertical)`
  @media ${laptopAndAbove} {
    align-items: flex-start;
  }
`;

type Props = {
  task: SetCollectionTask_taskInterface;
  onClaim: () => void;
  onClick: (() => void) | null;
  loading: boolean;
  selected?: boolean;
};

export const SetCollectionTask = ({
  task,
  onClaim,
  loading,
  onClick,
  selected,
}: Props) => {
  const commonGameSet = useCommonGameSet();
  const getDisplayCardSetName = useGetDisplayCardSetName();

  if (
    !isType(task, 'CardsCountTask') &&
    !isType(task, 'CompleteCollectionsCountTask')
  ) {
    return null;
  }

  const { rewardConfigs, aasmState } = task;

  const rewardConfig = rewardConfigs.at(0);
  const isCompleted = aasmState === ManagerProgressionTaskState.COMPLETED;
  const isClaimed = aasmState === ManagerProgressionTaskState.CLAIMED;

  const leagueName =
    (isType(task, 'CardsCountTask') && task.competition?.displayName) ||
    (isType(task, 'CompleteCollectionsCountTask') &&
      task.competition?.displayName) ||
    // TODO: Get this from backend (TaskInterface.cardSet ?)
    getDisplayCardSetName(commonGameSet.cardSet.activeSlug);

  if (!isCompleted && !isClaimed) {
    const { liveScore, scoreToReach = 1 } = task;

    return (
      <>
        <InProgressTaskWrapper className={classNames({ selected })}>
          <Vertical>
            <Vertical>
              <BrandXS
                bold
                color={getTaskCompetitionColor(task, 'var(--c-set-main-color)')}
              >
                {leagueName}
              </BrandXS>
              <StyledLinkOverlay
                as="button"
                onClick={onClick || undefined}
                disabled={!onClick}
              >
                <SetCollectionTaskDescription task={task} />
              </StyledLinkOverlay>
            </Vertical>
            <SBHorizontal gap={2}>
              <ProgressSection>
                <StyledProgress
                  value={liveScore}
                  max={scoreToReach}
                  color="var(--c-set-main-color)"
                  bgColor="var(--c-nd-150-opaque)"
                />
                <Horizontal>
                  <LabelS bold>{liveScore}</LabelS>
                  <LabelS color="var(--c-nd-600)">/</LabelS>
                  <LabelS color="var(--c-nd-600)" bold>
                    {scoreToReach}
                  </LabelS>
                  {isType(task, 'CardsCountTask') ? (
                    <ScarcityIcon
                      rarity={Rarity.common}
                      // TODO: Get this from backend (CardsCountTask.cardSet ?)
                      cardSet={commonGameSet.cardSet.activeSlug}
                    />
                  ) : (
                    <Collection size={2} />
                  )}
                </Horizontal>
              </ProgressSection>
              {!!rewardConfig && (
                <AnyRewardConfig
                  anyRewardConfig={rewardConfig}
                  variant={Variant.INLINE_CHIP}
                />
              )}
            </SBHorizontal>
          </Vertical>
        </InProgressTaskWrapper>
      </>
    );
  }

  return (
    <Container className={classNames({ claimed: isClaimed, selected })}>
      <StyledLightedBezel
        color="var(--c-set-main-color)"
        bezelSize={16}
        radius={16}
        borderWidth={1}
        background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, rgba(var(--c-rgb-black), 0.2) 0) 0 0/5px 5px"
        boxShadow={
          isClaimed && !selected
            ? undefined
            : '0px 0px 8px 2px rgba(23, 158, 255, 0.2) inset'
        }
      >
        <CardContent>
          {!!rewardConfig && (
            <AnyRewardConfig
              anyRewardConfig={rewardConfig}
              variant={Variant.CHIP}
            />
          )}
          <ContentWrapper>
            <BrandXS
              bold
              color={getTaskCompetitionColor(task, 'var(--c-set-main-color)')}
            >
              {leagueName}
            </BrandXS>
            <StyledLinkOverlay
              as="button"
              onClick={onClick || undefined}
              disabled={!onClick}
            >
              <SetCollectionTaskDescription task={task} />
            </StyledLinkOverlay>
            {!!isCompleted && (
              <ClaimButton task={task} onClaim={onClaim} loading={loading} />
            )}
          </ContentWrapper>
        </CardContent>
      </StyledLightedBezel>
    </Container>
  );
};

SetCollectionTask.fragments = {
  taskInterface: gql`
    fragment SetCollectionTask_taskInterface on TaskInterface {
      id
      aasmState
      iconUrl
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        scoreToReach
        competition {
          slug
          displayName
        }
      }
      ... on CardsCountTask {
        id
        liveScore
        scoreToReach
        competition {
          slug
          displayName
        }
      }
      ...ClaimButton_taskInterface
      ...getTaskCompetitionColor_taskInterface
      ...SetCollectionTaskDescription_taskInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${ClaimButton.fragments.taskInterface}
    ${getTaskCompetitionColor.fragments.taskInterface}
    ${SetCollectionTaskDescription.fragments.taskInterface}
  ` as TypedDocumentNode<SetCollectionTask_taskInterface>,
};
